import React from 'react';
import { Helmet } from 'react-helmet-async';

export const SITE_URL = 'https://www.vforuglobal.com';
export const SITE_NAME = 'VforU Global';
export const BRAND_PHONE = '+91 7339209222';
export const BRAND_PHONE_TEL = 'tel:+917339209222';
export const BRAND_WHATSAPP = 'https://wa.me/917339209222';
export const BRAND_EMAIL = 'info@vforuglobal.com';

/**
 * Organization + LocalBusiness schema, shared across every page so each
 * page independently carries full entity information (helps AEO/GEO —
 * answer engines and AI crawlers that only fetch one page still get the
 * complete business identity, not just the homepage).
 */
export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'LocalBusiness'],
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo512.png`,
    image: `${SITE_URL}/logo512.png`,
    description:
      'VforU Global is a full-service consultancy firm helping Indian businesses with Certification (ISO, BIS, FSSAI, BEE), IT Solutions, and Energy Consultancy.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Chennai',
      addressRegion: 'Tamil Nadu',
      addressCountry: 'IN',
    },
    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: BRAND_PHONE,
      contactType: 'customer service',
      availableLanguage: ['English', 'Tamil', 'Hindi'],
    },
    telephone: BRAND_PHONE,
    email: BRAND_EMAIL,
    priceRange: '$$',
    // TODO: add real profile URLs (LinkedIn, Google Business Profile, Instagram, etc.)
    // Populating this strengthens entity recognition for GEO/AEO citation confidence.
    sameAs: [],
  };
}

/** BreadcrumbList schema builder for a given page path/name. */
export function breadcrumbSchema(items) {
  // items: [{ name: 'Home', path: '/' }, { name: 'Services', path: null }, ...]
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      ...(item.path ? { item: `${SITE_URL}${item.path === '/' ? '' : item.path}` } : {}),
    })),
  };
}

/** Service schema builder for a service page. */
export function serviceSchema({ name, description, path }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: name,
    name,
    description,
    url: `${SITE_URL}${path}`,
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: { '@type': 'Country', name: 'India' },
  };
}

/** FAQPage schema builder. faqs: [{ q, a }] */
export function faqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

/**
 * Per-page SEO tags: title, description, canonical, Open Graph, Twitter Card,
 * plus any number of JSON-LD structured-data blocks.
 *
 * This is what was missing before — every route now gets its OWN title,
 * description, and canonical instead of inheriting the homepage's.
 */
export default function SEO({ title, description, path = '/', image, structuredData = [] }) {
  const url = `${SITE_URL}${path === '/' ? '' : path}`;
  const ogImage = image || `${SITE_URL}/logo512.png`;
  const schemas = Array.isArray(structuredData) ? structuredData : [structuredData];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}